import { Loading } from "@mozioinc/ui";
import { ErrorBoundary } from "@sentry/react";
import { createRouter, defaultParseSearch } from "@tanstack/react-router";
import { GeneralError } from "../components";
import { queryClient } from "../utils/query-client";
import { notFoundRoute } from "./404";
import { bookRoute } from "./book";
import { chatRoute } from "./chat";
import {
  customerFeedbackRoute,
  customerFeedbackSuccessRoute,
} from "./customer-feedback";
import { langReservationsAppleWalletRoute } from "./lang-reservations-apple-wallet";
import { langReservationsGoogleWalletRoute } from "./lang-reservations-google-wallet";
import { localeRoute } from "./locale";
import { referralRoute } from "./referral";
import { reservationsAppleWalletRoute } from "./reservations-apple-wallet";
import { reservationsGoogleWalletRoute } from "./reservations-google-wallet";
import { reservationsVoucherRoute } from "./reservations-voucher";
import { rootRoute } from "./root";
import { searchRoute } from "./search";
import { trackingRoute } from "./tracking";
import { updatePaymentRoute } from "./update-payment";
import { whiteLabelRoute } from "./white-label";

const routeTree = rootRoute.addChildren([
  localeRoute.addChildren([
    bookRoute,
    chatRoute,
    searchRoute,
    whiteLabelRoute,
    customerFeedbackRoute,
    customerFeedbackSuccessRoute,
  ]),
  trackingRoute,
  referralRoute,
  reservationsVoucherRoute,
  updatePaymentRoute,
  reservationsAppleWalletRoute,
  reservationsGoogleWalletRoute,
  langReservationsAppleWalletRoute,
  langReservationsGoogleWalletRoute,
]);

// Partner links encode spaces as "+"; decodeURIComponent alone doesn't handle that.
const parseSearch = (searchStr: string) =>
  defaultParseSearch(searchStr.replaceAll("+", "%20"));

export const router = createRouter({
  routeTree,
  parseSearch,
  defaultPendingComponent: () => <Loading className="h-screen" />,
  defaultErrorComponent: () => (
    <ErrorBoundary>
      <GeneralError className="h-screen" />
    </ErrorBoundary>
  ),
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  notFoundRoute,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
